// src/pages/Settings/index.js
import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Form,
  FormGroup,
  Input,
  Label,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Button,
  Alert,
  Spinner,
} from "reactstrap";
import classnames from "classnames";
import { Formik } from "formik";
import * as Yup from "yup";
import {
  getSettings,
  updateSettings,
} from "../../helpers/eirna_backend_helper";
import { FadeLoader } from "react-spinners";

const Settings = () => {
  const [activeTab, setActiveTab] = useState("1");
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [settingsData, setSettingsData] = useState({});

  // Fetch settings on component mount
  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      const response = await getSettings();
      setSettingsData(response);
    } catch (error) {
      setErrorMessage("Failed to load settings. Please try again later.");
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  // Validation schema
  const validationSchema = Yup.object({
    // General Settings
    background_colour: Yup.string().required("Background colour is required"),
    title_size: Yup.number()
      .min(1, "Must be greater than 0")
      .required("Title size is required"),
    subheading_size: Yup.number()
      .min(1, "Must be greater than 0")
      .required("Subheading size is required"),
    axis_label_size: Yup.number()
      .min(1, "Must be greater than 0")
      .required("Axis label size is required"),
    marker_size: Yup.number()
      .min(1, "Must be greater than 0")
      .required("Marker size is required"),
    legend_size: Yup.number()
      .min(1, "Must be greater than 0")
      .required("Legend size is required"),
    cds_marker_width: Yup.number()
      .min(1, "Must be greater than 0")
      .required("CDS marker width is required"),
    cds_marker_colour: Yup.string().required("CDS marker colour is required"),

    // Single gene/transcript plot
    uag_col: Yup.string().required("UAG colour is required"),
    uaa_col: Yup.string().required("UAA colour is required"),
    uga_col: Yup.string().required("UGA colour is required"),
    ribo_linewidth: Yup.number()
      .min(1, "Must be greater than 0")
      .required("Riboseq line width is required"),

    // Single gene/transcript comparison plot
    comp_uag_col: Yup.string().required("UAG colour is required"),
    comp_uaa_col: Yup.string().required("UAA colour is required"),
    comp_uga_col: Yup.string().required("UGA colour is required"),

    // Metainformation settings
    readlength_colour: Yup.string().required("Readlength colour is required"),
    metagene_fiveprime_colour: Yup.string().required(
      "Metagene fiveprime colour is required"
    ),
    metagene_threeprime_colour: Yup.string().required(
      "Metagene threeprime colour is required"
    ),
    nuc_comp_a_col: Yup.string().required("Nucleotide A colour is required"),
    nuc_comp_t_col: Yup.string().required("Nucleotide T colour is required"),
    nuc_comp_g_col: Yup.string().required("Nucleotide G colour is required"),
    nuc_comp_c_col: Yup.string().required("Nucleotide C colour is required"),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setUpdating(true);
      setSuccessMessage("");
      setErrorMessage("");

      await updateSettings(values);
      setSuccessMessage("Settings updated successfully!");

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      setErrorMessage("Failed to update settings. Please try again.");
      console.error("Error updating settings:", error);
    } finally {
      setUpdating(false);
      setSubmitting(false);
    }
  };

  const tabChange = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const renderFormField = (name, label, description, type = "text", formik) => (
    <Col lg={6} className="mb-1">
      <FormGroup>
        <Label htmlFor={name} className="form-label fw-semibold mb-2">
          {label}:
        </Label>
        <Input
          type={type}
          name={name}
          id={name}
          value={formik.values[name] || ""}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          invalid={formik.touched[name] && formik.errors[name]}
          style={{
            borderRadius: "2px",
            border: "1px solid #d1d5db",
            fontSize: "14px",
            padding: "8px 12px",
          }}
        />
        {formik.touched[name] && formik.errors[name] ? (
          <div className="text-danger mt-1" style={{ fontSize: "13px" }}>
            {formik.errors[name]}
          </div>
        ) : (
          <p
            className="text-muted mt-1 mb-0"
            style={{ fontSize: "10px", lineHeight: "1.4" }}
          >
            {description}
          </p>
        )}
      </FormGroup>
    </Col>
  );

  document.title = "Settings | EIRNABio Connect";

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="loading-overlay">
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "75vh",
              }}
            >
              <FadeLoader color="#1A6D52" size={50} />
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Formik
          initialValues={settingsData}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {(formik) => (
            <Form onSubmit={formik.handleSubmit}>
              {/* Page Header with Update Button */}
              <Row className="mb-3 align-items-center">
                <Col lg={6}>
                  <h4 className="mb-0 fw-bold" style={{ color: "#374151" }}>
                    Settings
                  </h4>
                </Col>
                <Col lg={6} className="text-end">
                  <Button
                    type="submit"
                    disabled={formik.isSubmitting || updating}
                    size="md"
                    color="primary"
                  >
                    {updating ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Updating...
                      </>
                    ) : (
                      <>
                        <i className="ri-save-line me-2"></i>
                        Update Settings
                      </>
                    )}
                  </Button>
                </Col>
              </Row>

              {/* Main Content */}
              <Row>
                <Col lg={12}>
                  <Card
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: "2px",
                      boxShadow: "0 1px 3px rgba(127, 125, 125, 0.1)",
                    }}
                  >
                    <CardHeader
                      style={{
                        backgroundColor: "#f8fafc",
                        borderBottom: "1px solid #e5e7eb",
                      }}
                    >
                      <Nav
                        className="nav-tabs-custom rounded card-header-tabs border-bottom-0"
                        role="tablist"
                      >
                        <NavItem>
                          <NavLink
                            className={classnames({
                              active: activeTab === "1",
                            })}
                            onClick={() => tabChange("1")}
                            style={{
                              color: activeTab === "1" ? "#1A6D52" : "#6b7280",
                              borderColor:
                                activeTab === "1" ? "#1A6D52" : "transparent",
                              fontWeight: activeTab === "1" ? "600" : "500",
                            }}
                          >
                            <i className="ri-settings-3-line me-2 "></i>
                            General settings
                          </NavLink>
                        </NavItem>
                        <NavItem>
                          <NavLink
                            className={classnames({
                              active: activeTab === "2",
                            })}
                            onClick={() => tabChange("2")}
                            style={{
                              color: activeTab === "2" ? "#1A6D52" : "#6b7280",
                              borderColor:
                                activeTab === "2" ? "#1A6D52" : "transparent",
                              fontWeight: activeTab === "2" ? "600" : "500",
                            }}
                          >
                            <i className="ri-line-chart-line me-2"></i>
                            Single gene/transcript plot
                          </NavLink>
                        </NavItem>
                        <NavItem>
                          <NavLink
                            className={classnames({
                              active: activeTab === "3",
                            })}
                            onClick={() => tabChange("3")}
                            style={{
                              color: activeTab === "3" ? "#1A6D52" : "#6b7280",
                              borderColor:
                                activeTab === "3" ? "#1A6D52" : "transparent",
                              fontWeight: activeTab === "3" ? "600" : "500",
                            }}
                          >
                            <i className="ri-bar-chart-2-line me-2"></i>
                            Single gene/transcript comparison plot
                          </NavLink>
                        </NavItem>
                        <NavItem>
                          <NavLink
                            className={classnames({
                              active: activeTab === "4",
                            })}
                            onClick={() => tabChange("4")}
                            style={{
                              color: activeTab === "4" ? "#1A6D52" : "#6b7280",
                              borderColor:
                                activeTab === "4" ? "#1A6D52" : "transparent",
                              fontWeight: activeTab === "4" ? "600" : "500",
                            }}
                          >
                            <i className="ri-information-line me-2"></i>
                            Metainformation settings
                          </NavLink>
                        </NavItem>
                      </Nav>
                    </CardHeader>

                    <CardBody style={{ padding: "32px" }}>
                      {/* Alerts */}
                      {successMessage && (
                        <Alert
                          color="success"
                          className="mb-4"
                          style={{
                            backgroundColor: "#f0fdf4",
                            borderColor: "#bbf7d0",
                            color: "#166534",
                            borderRadius: "6px",
                          }}
                        >
                          <i className="ri-check-circle-line me-2"></i>
                          {successMessage}
                        </Alert>
                      )}

                      {errorMessage && (
                        <Alert
                          color="danger"
                          className="mb-4"
                          style={{
                            backgroundColor: "#fef2f2",
                            borderColor: "#fecaca",
                            color: "#dc2626",
                            borderRadius: "6px",
                          }}
                        >
                          <i className="ri-error-warning-line me-2"></i>
                          {errorMessage}
                        </Alert>
                      )}

                      <TabContent activeTab={activeTab}>
                        {/* General Settings Tab */}
                        <TabPane tabId="1">
                          <Row>
                            {renderFormField(
                              "background_colour",
                              "Background colour",
                              "The background colour for almost all plots on EIRNABio Connect. Can be set to colour names like white, black, grey or hex (#0f0f0f)",
                              "text",
                              formik
                            )}

                            {renderFormField(
                              "legend_size",
                              "Legend Size",
                              "This controls the size of the labels on plots with interactive legends",
                              "text",
                              formik
                            )}
                          </Row>

                          <Row>
                            {renderFormField(
                              "title_size",
                              "Title Size",
                              "This controls the size of the plot titles",
                              "number",
                              formik
                            )}

                            {renderFormField(
                              "cds_marker_width",
                              "CDS Markers Width",
                              "This controls the width of the CDS markers on the single transcript and comparison plots",
                              "number",
                              formik
                            )}
                          </Row>

                          <Row>
                            {renderFormField(
                              "subheading_size",
                              "Subheading Size",
                              "This controls the size of the subheadings, where transcript id and length are displayed",
                              "number",
                              formik
                            )}

                            {renderFormField(
                              "cds_marker_colour",
                              "CDS Markers Colour",
                              "This controls the colour of the CDS markers on the single transcript and comparison plots",
                              "text",
                              formik
                            )}
                          </Row>

                          <Row>
                            {renderFormField(
                              "axis_label_size",
                              "Axis Label Size",
                              "This controls the size of the axis labels",
                              "number",
                              formik
                            )}

                            {renderFormField(
                              "marker_size",
                              "Tick Marker Size",
                              "This controls the size of the numbers on the x and y axis",
                              "number",
                              formik
                            )}
                          </Row>
                        </TabPane>

                        <TabPane tabId="2">
                          <Row>
                            {renderFormField(
                              "uag_col",
                              "UAG Colour",
                              "This sets the colour of the UAG stop codons in the open reading frame architecture. Can be set to colour names such as white, black, grey or hex values such as #d6d6d6",
                              "text",
                              formik
                            )}

                            {renderFormField(
                              "uaa_col",
                              "UAA Colour",
                              "This sets the colour of the UAA stop codons in the open reading frame architecture. Can be set to colour names such as white, black, grey or hex values such as #d6d6d6",
                              "text",
                              formik
                            )}
                          </Row>
                          <Row>
                            {renderFormField(
                              "uga_col",
                              "UGA Colour",
                              "This sets the colour of the UGA stop codons in the open reading frame architecture. Can be set to colour names such as white, black, grey or hex values such as #d6d6d6",
                              "text",
                              formik
                            )}

                            {renderFormField(
                              "ribo_linewidth",
                              "Riboseq line width",
                              "This controls the width of the red, green and blue ribo-seq lines",
                              "number",
                              formik
                            )}
                          </Row>
                        </TabPane>

                        {/* Single Gene/Transcript Comparison Plot Tab */}
                        <TabPane tabId="3">
                          <Row>
                            {renderFormField(
                              "comp_uag_col",
                              "UAG Colour",
                              "This sets the colour of the UAG stop codons in the open reading frame architecture. Can be set to colour names such as white, black, grey or hex values such as #d6d6d6",
                              "text",
                              formik
                            )}

                            {renderFormField(
                              "comp_uaa_col",
                              "UAA Colour",
                              "This sets the colour of the UAA stop codons in the open reading frame architecture. Can be set to colour names such as white, black, grey or hex values such as #d6d6d6",
                              "text",
                              formik
                            )}
                          </Row>

                          <Row>
                            {renderFormField(
                              "comp_uga_col",
                              "UGA Colour",
                              "This sets the colour of the UGA stop codons in the open reading frame architecture. Can be set to colour names such as white, black, grey or hex values such as #d6d6d6",
                              "text",
                              formik
                            )}
                          </Row>
                        </TabPane>

                        {/* Metainformation Settings Tab */}
                        <TabPane tabId="4">
                          <Row>
                            {renderFormField(
                              "readlength_colour",
                              "Readlength distribution bar colour",
                              "This sets the colour of the bars in the read length distribution plot. Can be set to colour names such as white, black, grey or hex values such as #d6d6d6",
                              "text",
                              formik
                            )}

                            {renderFormField(
                              "metagene_fiveprime_colour",
                              "Metagene fiveprime bar colour",
                              "This sets the colour of the bars in the metagene profiles for the fiveprime ends. Can be set to colour names such as white, black, grey or hex values such as #d6d6d6",
                              "text",
                              formik
                            )}
                          </Row>

                          <Row>
                            {renderFormField(
                              "metagene_threeprime_colour",
                              "Metagene threeprime bar colour",
                              "This sets the colour of the bars in the metagene profiles for the threeprime ends. Can be set to colour names such as white, black, grey or hex values such as #d6d6d6",
                              "text",
                              formik
                            )}

                            {renderFormField(
                              "nuc_comp_a_col",
                              "Nucleotide composition A colour",
                              "This sets the colour of the line for the A nucleotide in the nucleotide composition plots. Can be set to colour names such as white, black, grey or hex values such as #d6d6d6",
                              "text",
                              formik
                            )}
                          </Row>

                          <Row>
                            {renderFormField(
                              "nuc_comp_t_col",
                              "Nucleotide composition T colour",
                              "This sets the colour of the line for the T nucleotide in the nucleotide composition plots. Can be set to colour names such as white, black, grey or hex values such as #d6d6d6",
                              "text",
                              formik
                            )}

                            {renderFormField(
                              "nuc_comp_g_col",
                              "Nucleotide composition G colour",
                              "This sets the colour of the line for the G nucleotide in the nucleotide composition plots. Can be set to colour names such as white, black, grey or hex values such as #d6d6d6",
                              "text",
                              formik
                            )}
                          </Row>

                          <Row>
                            {renderFormField(
                              "nuc_comp_c_col",
                              "Nucleotide composition C colour",
                              "This sets the colour of the line for the C nucleotide in the nucleotide composition plots. Can be set to colour names such as white, black, grey or hex values such as #d6d6d6",
                              "text",
                              formik
                            )}
                          </Row>
                        </TabPane>
                      </TabContent>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </Container>
    </div>
  );
};

export default Settings;
